import { Injectable } from '@angular/core';

import { AdditionalInformation } from '@bpos';
import { PromoTokenType, PromotionToken, SignPost, SignPostType } from '@bpos/v1/my-bets';
import { RewardTargetType } from '@bpos/v1/sports-promo/tokens';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import Decimal from 'decimal.js';

import { PayoutType, RewardTokenType } from '../../../../lib/tokens-base/token-base.models';
import { TokensSummaryService } from '../../../../lib/tokens-base/tokens-summary.service';
import { ISucceededSlipResult } from '../../betplacement/models';
import { convertPayoutType, convertTokenType } from '../../reward-tokens/helpers/token-types.helper';
import { BetAndGetEdsToken, BetAndGetToken, PromoTokenPlaceBetSignPostingInfo, RiskFreeToken } from '../../reward-tokens/reward-tokens.model';
import { IResultSucceededSlipSummaryViewModel } from '../models/result-succeeded-slip.model';
import { getTokenWinningsBoost } from '../utils/result-tokens.utils';

@Injectable({ providedIn: 'root' })
export class UsedTokenPayoutSummaryService {
    private readonly logger: SportsRemoteLogger;

    constructor(
        private tokensSummaryService: TokensSummaryService,
        loggerFactory: LoggerFactory,
    ) {
        this.logger = loggerFactory.getLogger('UsedTokenPayoutSummaryService');
    }

    getViewModelWithTokenInfo(slip: ISucceededSlipResult, viewModel: IResultSucceededSlipSummaryViewModel): IResultSucceededSlipSummaryViewModel {
        const updatedViewModel = { ...viewModel };

        this.addTokenSlipSummary(updatedViewModel, slip);

        return updatedViewModel;
    }

    private addTokenSlipSummary(viewModel: IResultSucceededSlipSummaryViewModel, succeededSlip: ISucceededSlipResult): void {
        if (succeededSlip.isFreebet) {
            viewModel.rewardTokenInfo = { usedRewardTokenType: RewardTokenType.FreeBet, usedRewardTokenTargetType: RewardTargetType.NonWelcomeOffer };

            return;
        }

        if (succeededSlip.promoTokens.length > 0) {
            this.addPromoTokensSummary(viewModel, succeededSlip);
        }

        if (!succeededSlip.edsPromoTokens.length) {
            return;
        }

        this.addBetAndGetTokenSummary(viewModel, succeededSlip);
    }

    private addPromoTokensSummary(viewModel: IResultSucceededSlipSummaryViewModel, slip: ISucceededSlipResult): void {
        const token = slip.promoTokens[0];
        const tokenType = convertTokenType(token.tokenType);

        if (!tokenType) {
            return;
        }

        const placeBetSignPostData = this.getPlaceBetSignPostingDetails<PromoTokenPlaceBetSignPostingInfo>(slip.signPostings) ?? {};

        viewModel.rewardTokenInfo = { usedRewardTokenType: tokenType, usedRewardTokenTargetType: placeBetSignPostData.rewardTargetType };

        this.addPromoTokenPayoutSummary(viewModel, token, slip, placeBetSignPostData);
    }

    private addPromoTokenPayoutSummary(
        data: IResultSucceededSlipSummaryViewModel,
        token: PromotionToken,
        slip: ISucceededSlipResult,
        placeBetSignPostData: PromoTokenPlaceBetSignPostingInfo,
    ): void {
        if (token.tokenType === PromoTokenType.RiskFree) {
            const riskFreePayoutSummary = this.getRiskFreePayoutSummary(token, slip, placeBetSignPostData);

            data.rewardTokenInfo = { ...data.rewardTokenInfo!, rewardPayoutSummary: riskFreePayoutSummary };

            return;
        }

        const hasPositiveWinnings = data.possibleWinnings.value && data.possibleWinnings.value.greaterThan(0);
        if (!hasPositiveWinnings) {
            return;
        }

        if (token.tokenType === PromoTokenType.OddsBoost) {
            this.addOddsBoostTokenPayoutSummary(data, token);

            return;
        }

        if (token.tokenType === PromoTokenType.AccaBoost) {
            this.addAccaBoostTokenPayoutSummary(data, token);

            return;
        }
    }

    private addOddsBoostTokenPayoutSummary(data: IResultSucceededSlipSummaryViewModel, token: PromotionToken): void {
        const winningBoost = getTokenWinningsBoost(token);
        const payoutType = convertPayoutType(token.payoutType) || PayoutType.Bonus;
        const summary = this.tokensSummaryService.getBoostedWinningsSummary(payoutType, winningBoost);

        data.possibleWinnings.value = data.possibleWinnings.value.add(winningBoost);
        data.possibleWinningsGross = data.possibleWinningsGross ? data.possibleWinningsGross.add(winningBoost) : data.possibleWinningsGross;

        data.rewardTokenInfo = { ...data.rewardTokenInfo!, rewardPayoutSummary: summary };
    }

    private addAccaBoostTokenPayoutSummary(data: IResultSucceededSlipSummaryViewModel, token: PromotionToken): void {
        const ratio = parseFloat(this.getTokenInformationItemValue(token, 'AccaBoostRatio') ?? '0');
        const accaBoostSummary = this.tokensSummaryService.getAccaBoostSummaryByRatio(ratio);
        const boostedWinnings = this.getTokenInformationItemValue(token, 'BoostedWinnings') ?? '0';

        data.possibleWinnings = {
            ...data.possibleWinnings,
            boostedValue: new Decimal(boostedWinnings).add(data.possibleWinnings.boostedValue ?? 0),
        };

        data.rewardTokenInfo = { ...data.rewardTokenInfo!, rewardPayoutSummary: accaBoostSummary, boostedByRatio: ratio };
    }

    private getTokenInformationItemValue(token: { additionalInformation: AdditionalInformation }, key: string): string | undefined {
        return token.additionalInformation.informationItems.find((item) => item.key === key)?.value;
    }

    private addBetAndGetTokenSummary(data: IResultSucceededSlipSummaryViewModel, slip: ISucceededSlipResult): void {
        if (!slip.signPostings.length) {
            const placeBetSignPost = this.getTokenInformationItemValue(slip.edsPromoTokens[0], 'PlaceBetSignPostingData');
            const placeBetSignPostVersion = Number(this.getTokenInformationItemValue(slip.edsPromoTokens[0], 'PlaceBetSignPostingVersion') ?? 1);

            slip.signPostings = placeBetSignPost
                ? [{ type: SignPostType.BetPlacement, version: placeBetSignPostVersion, data: placeBetSignPost }]
                : [];
        }

        const betAndGetSignpostData = this.getPlaceBetSignPostingDetails<BetAndGetEdsToken>(slip.signPostings);

        if (!betAndGetSignpostData) {
            return;
        }

        const rewardTargetType = betAndGetSignpostData.rewardTargetType ?? RewardTargetType.NonWelcomeOffer;

        const betAndGetToken = {
            payoutType: betAndGetSignpostData.payoutType,
            rewardTriggerType: betAndGetSignpostData.triggerType,
            payoutValue: betAndGetSignpostData.payoutDetails.value,
        } as BetAndGetToken;

        const betAndGetSummary = this.tokensSummaryService.getBetAndGetSummary(betAndGetToken);

        data.rewardTokenInfo = {
            usedRewardTokenType: RewardTokenType.BetAndGet,
            usedRewardTokenTargetType: rewardTargetType,
            rewardPayoutSummary: betAndGetSummary,
        };
    }

    private getRiskFreePayoutSummary(
        token: PromotionToken,
        slip: ISucceededSlipResult,
        { noOfSlabs, slabAmount }: PromoTokenPlaceBetSignPostingInfo,
    ): string {
        const riskFreebetToken: Partial<RiskFreeToken> = {
            tokenType: RewardTokenType.RiskFreeBet,
            payoutType: (<unknown>token.payoutType) as PayoutType,
            maximumPayout: parseFloat(this.getTokenInformationItemValue(token, 'RiskFreeMaxCompensation') ?? '0'),
            riskFreePercentage: parseInt(this.getTokenInformationItemValue(token, 'RiskFreePercentage') ?? '0', 10),
        };

        return this.tokensSummaryService.getRiskFreeSummary(
            riskFreebetToken.payoutType!,
            riskFreebetToken.maximumPayout!,
            riskFreebetToken.riskFreePercentage,
            slip.stake,
            noOfSlabs,
            slabAmount,
        );
    }

    getPlaceBetSignPostingDetails<T>(signPostings: SignPost[]): T | undefined {
        const signPostDetails = signPostings.find((signPost) => signPost.type === SignPostType.BetPlacement);

        try {
            if (signPostDetails?.data) {
                return JSON.parse(signPostDetails.data) as T;
            }
        } catch (error) {
            this.logger.error(error, 'Failed to parse PlaceBetSignPostingData.');
        }

        return;
    }
}
